import { stageOf, countByStage, STAGE_KEYS } from '../lib/ticketStage';
import TicketStageTabs from '../components/TicketStageTabs';
import { rememberOrder } from '../lib/ticketOrder';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import RequesterName from '../components/RequesterName';
import PeoplePicker from '../components/PeoplePicker';
import { SortHeader, SortMenu, Sheet, nextSort, usePhone } from '../components/ListSort';
import './TicketsList.css';
import { fmtDate } from '../lib/when';

/**
 * ALL TICKETS, sorted and filtered by the server.
 *
 * Gordon, 23 October: "in the all tickets view I don't have the option to sort
 * by requester, assigned to and contractor. Please add these filters too."
 *
 * The page used to ask GET /tickets for its list with no parameters, and that
 * endpoint has always answered with the newest TWENTY - so "All tickets" was
 * twenty tickets, and every count, sort and page on it was computed over those.
 * It now asks for one page at a time with the sort and every filter applied in
 * the database (backend lib/ticket-list.js), and gets back the total, the count
 * per stage and the people the pickers offer.
 *
 * THE URL IS THE STATE. Every filter, the sort and the page live in the query
 * string, so a view can be bookmarked, sent to somebody, or come back to with
 * the back button, and two tabs can hold two different views. The page did
 * not remember filters between visits before, and still does not: a view that
 * quietly re-applies last week's filter is how somebody concludes a ticket has
 * vanished.
 *
 * If the server answers with the old bare array (a backend older than this
 * page), everything is done in the browser as before, so the page is never
 * worse than it was.
 */

const LIST_KEYS = ['status', 'priority', 'department', 'category'];
const PEOPLE_KEYS = ['requester', 'assignee', 'contractor'];
const FILTER_KEYS = ['q', 'stage', ...LIST_KEYS, ...PEOPLE_KEYS];

// The direction a column starts in when first pressed: dates and urgency
// newest / most urgent first, everything else A-Z.
const FIRST_DIR = { created_at: 'desc', priority: 'desc' };

const SORTS = [
  { key: 'created_at', label: 'Created', dir: 'desc' },
  { key: 'ticket_number', label: 'ID' },
  { key: 'title', label: 'Title' },
  { key: 'requester', label: 'Requester' },
  { key: 'assignee', label: 'Assigned to' },
  { key: 'contractor', label: 'Contractor' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority', dir: 'desc' },
  { key: 'category', label: 'Category' }
];
const SORT_KEYS = SORTS.map((s) => s.key);
const PAGE_SIZES = ['20', '50', '100', 'all'];
const ALL_LABEL = { status: 'All Statuses', priority: 'All Priorities',
  department: 'All Departments', category: 'All Categories' };

const csv = (v) => (v ? String(v).split(',').map((s) => s.trim()).filter(Boolean) : []);
const slug = (v) => String(v || 'unknown').toLowerCase().replace(/\s+/g, '-');

/** The view, read from the URL. Unknown values fall back rather than break. */
function readView(params) {
  const sort = SORT_KEYS.includes(params.get('sort')) ? params.get('sort') : 'created_at';
  const d = params.get('dir');
  const dir = d === 'asc' || d === 'desc' ? d : (FIRST_DIR[sort] || 'asc');
  const size = PAGE_SIZES.includes(params.get('size')) ? params.get('size') : '20';
  const stage = STAGE_KEYS.includes(params.get('stage')) ? params.get('stage') : 'all';
  return {
    q: params.get('q') || '',
    stage,
    status: params.get('status') || 'all',
    priority: params.get('priority') || 'all',
    department: params.get('department') || 'all',
    category: params.get('category') || 'all',
    requester: csv(params.get('requester')),
    assignee: csv(params.get('assignee')),
    contractor: csv(params.get('contractor')),
    sort,
    dir,
    page: Math.max(1, parseInt(params.get('page'), 10) || 1),
    size
  };
}

/* ----------------------------------------------- the old, browser-side path */

const PRIORITY_RANK = { low: 1, medium: 2, high: 3, critical: 4 };
const STATUS_RANK = { 'awaiting approval': 1, open: 2, 'in progress': 3, 'on hold': 4,
  resolved: 5, approved: 6, closed: 7 };

function sortValue(t, key) {
  const low = (v) => (v ? String(v).trim().toLowerCase() || null : null);
  switch (key) {
    case 'ticket_number': return t.ticket_number || null;
    case 'title': return low(t.title);
    case 'requester': return low(t.requester_name);
    case 'assignee': return low(t.assigned_to_name);
    case 'contractor': return low(t.contractor_name);
    case 'status': return STATUS_RANK[low(t.status)] || 8;
    case 'priority': return PRIORITY_RANK[low(t.priority)] || null;
    case 'category': return low(t.category);
    default: return t.created_at ? new Date(t.created_at).getTime() : null;
  }
}

/** The server's rules, applied to an array: for a backend without them. */
function inBrowser(all, view) {
  const q = view.q.trim().toLowerCase();
  const people = (ids, id) => ids.length === 0
    || (id ? ids.includes(String(id)) : ids.includes('none'));
  const rows = all.filter((t) => (!q || `${t.title} ${t.ticket_number} ${t.description || ''}`
    .toLowerCase().includes(q))
    && (view.status === 'all' || t.status === view.status)
    && (view.priority === 'all' || t.priority === view.priority)
    && (view.department === 'all' || t.department === view.department)
    && (view.category === 'all' || t.category === view.category)
    && people(view.requester, t.requester_id || t.requester_contractor_id)
    && people(view.assignee, t.assigned_to)
    && people(view.contractor, t.contractor_id));
  const counts = countByStage(rows);
  const staged = view.stage === 'all' ? rows : rows.filter((t) => stageOf(t) === view.stage);
  const sign = view.dir === 'asc' ? 1 : -1;
  staged.sort((a, b) => {
    const x = sortValue(a, view.sort);
    const y = sortValue(b, view.sort);
    if (x === null && y !== null) return 1;      // nobody last, both ways
    if (y === null && x !== null) return -1;
    if (x !== null && y !== null && x !== y) return (x < y ? -1 : 1) * sign;
    return new Date(b.created_at) - new Date(a.created_at);
  });
  const size = view.size === 'all' ? Number.MAX_SAFE_INTEGER : Number(view.size);
  const start = (view.page - 1) * size;
  const optionsOf = (idKey, nameKey) => {
    const m = new Map();
    for (const t of all) {
      const id = t[idKey];
      if (!id) continue;
      const prev = m.get(id);
      if (prev) prev.n += 1; else m.set(id, { id, name: t[nameKey] || '(unnamed)', n: 1 });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  };
  return {
    tickets: staged.slice(start, start + size),
    total: staged.length,
    pages: Math.max(1, Math.ceil(staged.length / size)),
    stage_counts: counts,
    options: {
      requesters: optionsOf('requester_id', 'requester_name'),
      assignees: optionsOf('assigned_to', 'assigned_to_name'),
      contractors: optionsOf('contractor_id', 'contractor_name'),
      unassigned: all.filter((t) => !t.assigned_to).length,
      no_contractor: all.filter((t) => !t.contractor_id).length
    }
  };
}

/* ------------------------------------------------------------------ page */

/**
 * "Steps have no owner" (124; Gordon, 3 November): a catalogue request with
 * an open checklist step nobody owns. The server sends steps_unowned to
 * administrators only, and stops once every open step has an owner.
 */
function StepsUnowned() {
  return (
    <span className="tl-flag" data-testid="steps-unowned"
          title="A checklist step on this request has no owner - hand it to somebody on the ticket">
      Steps have no owner
    </span>
  );
}

function TicketsList() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [params, setParams] = useSearchParams();
  const view = useMemo(() => readView(params), [params]);
  const phone = usePhone(640);

  const [data, setData] = useState(null);       // the current page, as served
  const [options, setOptions] = useState(null); // the pickers' people
  const [legacy, setLegacy] = useState(null);   // the whole array, old backend only
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referenceData, setReferenceData] = useState({
    statuses: [], priorities: [], departments: [], categories: []
  });
  const [search, setSearch] = useState(view.q);
  const [sheetOpen, setSheetOpen] = useState(false);
  const optionsAsked = useRef(false);

  /** Change the view. Any filter change goes back to page one. */
  const update = useCallback((patch, { keepPage = false } = {}) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        const empty = v === undefined || v === null || v === '' || v === 'all'
          || (Array.isArray(v) && v.length === 0)
          || (k === 'page' && Number(v) === 1)
          || (k === 'size' && v === '20');
        if (empty) next.delete(k);
        else next.set(k, Array.isArray(v) ? v.join(',') : String(v));
      }
      if (!keepPage && !('page' in patch)) next.delete('page');
      return next;
    }, { replace: true });
  }, [setParams]);

  // The search box types into local state and reaches the URL a moment later,
  // so a request is not sent per keystroke.
  useEffect(() => { setSearch(view.q); }, [view.q]);
  useEffect(() => {
    if (search === view.q) return undefined;
    const id = setTimeout(() => update({ q: search.trim() }), 300);
    return () => clearTimeout(id);
  }, [search, view.q, update]);

  useEffect(() => {
    axios.get(`${API_URL}/reference-data`).then((r) => setReferenceData({
      statuses: r.data?.statuses || [],
      priorities: r.data?.priorities || [],
      departments: r.data?.departments || [],
      categories: r.data?.categories || []
    })).catch(() => {});
  }, [API_URL]);

  // The request, built from the view. One in flight at a time: a slower
  // answer to an older view must not overwrite a newer one.
  const query = useMemo(() => {
    const p = { envelope: 1, sort: view.sort, dir: view.dir, page: view.page, size: view.size };
    if (view.q) p.q = view.q;
    if (view.stage !== 'all') p.stage = view.stage;
    for (const k of LIST_KEYS) if (view[k] !== 'all') p[k] = view[k];
    for (const k of PEOPLE_KEYS) if (view[k].length) p[k] = view[k].join(',');
    return p;
  }, [view]);

  useEffect(() => {
    if (legacy) return undefined;     // an old backend: the browser does it all
    let live = true;
    // The pickers' people are asked for until they have arrived once - not
    // just once, or a first request that failed would leave them empty.
    const wantOptions = !optionsAsked.current;
    setLoading(true);
    axios.get(`${API_URL}/tickets`, { params: { ...query, ...(wantOptions ? { options: 1 } : {}) } })
      .then((r) => {
        if (!live) return;
        if (Array.isArray(r.data)) {
          // The server ignored `envelope` - an older backend. Ask for
          // everything once and do the rest here.
          return axios.get(`${API_URL}/tickets`, { params: { limit: 5000 } })
            .then((all) => { if (live) setLegacy(Array.isArray(all.data) ? all.data : r.data); });
        }
        setData(r.data);
        if (r.data?.options) { setOptions(r.data.options); optionsAsked.current = true; }
        setError(null);
        return undefined;
      })
      .catch((err) => {
        if (!live) return;
        setError(err.response?.data?.error || 'Could not load the tickets');
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [API_URL, query, legacy]);

  const shown = useMemo(() => (legacy ? inBrowser(legacy, view) : data), [legacy, view, data]);
  const pickers = legacy ? shown?.options : options;
  const tickets = shown?.tickets || [];
  // The order on screen, for the ticket page's previous / next ([ and ]).
  const orderKey = tickets.map((t) => t.id).join(',');
  useEffect(() => { if (orderKey) rememberOrder(orderKey.split(',')); }, [orderKey]);
  const total = shown?.total ?? 0;
  const pages = shown?.pages || 1;
  const stageCounts = shown?.stage_counts || null;

  // A page past the end - the filters narrowed under it - goes to the last one.
  useEffect(() => {
    if (shown && view.page > pages) update({ page: pages }, { keepPage: true });
  }, [shown, view.page, pages, update]);

  const narrowed = FILTER_KEYS.filter((k) => (Array.isArray(view[k])
    ? view[k].length > 0 : view[k] && view[k] !== 'all'));
  const sheetCount = narrowed.filter((k) => k !== 'q' && k !== 'stage').length;
  const clearAll = () => {
    setSearch('');
    update(Object.fromEntries(FILTER_KEYS.filter((k) => k !== 'stage').map((k) => [k, null])));
  };
  const onSort = (key) => update(nextSort(view, key, FIRST_DIR), { keepPage: false });

  const selectFor = (key, label, list) => (
    <select
      value={view[key]}
      onChange={(e) => update({ [key]: e.target.value })}
      className="toolbar-select"
      aria-label={`Filter by ${label.toLowerCase()}`}
      data-filter={key}
    >
      <option value="all">{ALL_LABEL[key]}</option>
      {list.map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
    </select>
  );

  const people = (inline) => (
    <>
      <PeoplePicker
        label="Requester" idBase="requester" inline={inline}
        options={pickers?.requesters || []} value={view.requester}
        onChange={(v) => update({ requester: v })}
      />
      <PeoplePicker
        label="Assigned to" idBase="assignee" inline={inline}
        options={pickers?.assignees || []} value={view.assignee}
        noneLabel="Unassigned" noneCount={pickers?.unassigned}
        onChange={(v) => update({ assignee: v })}
      />
      <PeoplePicker
        label="Contractor" idBase="contractor" inline={inline}
        options={pickers?.contractors || []} value={view.contractor}
        noneLabel="No contractor" noneCount={pickers?.no_contractor}
        onChange={(v) => update({ contractor: v })}
      />
    </>
  );

  const countLabel = (() => {
    const all = shown?.unfiltered_total ?? (legacy ? legacy.length : stageCounts?.all);
    if (narrowed.length === 0 || all === undefined) return `${total} ticket${total === 1 ? '' : 's'}`;
    return `${total} of ${all}`;
  })();

  const pageButtons = (() => {
    // A window around the current page, with the ends: 1 … 6 7 8 … 40.
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const set = new Set([1, pages, view.page - 1, view.page, view.page + 1]);
    const list = [...set].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
    const out = [];
    list.forEach((p, i) => { if (i && p - list[i - 1] > 1) out.push(`gap-${p}`); out.push(p); });
    return out;
  })();

  if (loading && !shown) {
    return <div className="container"><p>Loading tickets...</p></div>;
  }

  return (
    <div className="container tl" data-phone={String(phone)}>
      <div className="page-bar">
        <div className="page-bar-title">
          <h1 className="page-title">All Tickets</h1>
          <span className="page-bar-count" aria-live="polite">{countLabel}</span>
        </div>

        {/* WHERE THE WORK IS, with counts. The counts follow every filter
            except the stage itself, so with a contractor chosen the row reads
            "of Marco's tickets, this many at each stage". The stages are a
            partition, so they still add up to All. */}
        <TicketStageTabs
          tickets={[]}
          counts={stageCounts}
          stage={view.stage}
          onStage={(s) => update({ stage: s })}
          idPrefix="tl-stage"
        />

        {phone ? (
          <div className="toolbar tl-phone-tools">
            <input
              type="search"
              className="toolbar-search"
              placeholder="Search title, ID or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search tickets"
            />
            <button type="button" className="ls-filter-btn" onClick={() => setSheetOpen(true)}
                    aria-haspopup="dialog">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" /></svg>
              Filters
              {sheetCount > 0 && <span className="ls-count" aria-label={`${sheetCount} set`}>{sheetCount}</span>}
            </button>
            <SortMenu options={SORTS} sort={view.sort} dir={view.dir}
                      onChange={(s) => update(s)} />
          </div>
        ) : (
          <div className="toolbar">
            <input
              type="search"
              className="toolbar-search"
              placeholder="Search by title, ID, or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search tickets"
            />
            {selectFor('status', 'Status', referenceData.statuses)}
            {selectFor('priority', 'Priority', referenceData.priorities)}
            {selectFor('department', 'Department', referenceData.departments)}
            {selectFor('category', 'Category', referenceData.categories)}
            {people(false)}
            <select
              value={view.size}
              onChange={(e) => update({ size: e.target.value })}
              className="toolbar-select"
              aria-label="Tickets per page"
            >
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
              <option value="all">Show all</option>
            </select>
            {narrowed.some((k) => k !== 'stage') && (
              <button className="btn btn-secondary btn-small" onClick={clearAll}>Clear</button>
            )}
          </div>
        )}
      </div>

      <Sheet
        title="Filter tickets"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        footer={(
          <>
            <button type="button" className="ls-btn" onClick={clearAll}
                    disabled={narrowed.filter((k) => k !== 'stage').length === 0}>Clear all</button>
            <button type="button" className="ls-btn is-primary" onClick={() => setSheetOpen(false)}>
              Show {total} ticket{total === 1 ? '' : 's'}
            </button>
          </>
        )}
      >
        {people(true)}
        {[['status', 'Status', referenceData.statuses], ['priority', 'Priority', referenceData.priorities],
          ['department', 'Department', referenceData.departments],
          ['category', 'Category', referenceData.categories]].map(([k, label, list]) => (
            <label key={k} className="ls-field">
              {label}
              <select value={view[k]} onChange={(e) => update({ [k]: e.target.value })} data-filter={k}>
                <option value="all">Any {label.toLowerCase()}</option>
                {list.map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
              </select>
            </label>
        ))}
        <label className="ls-field">
          Per page
          <select value={view.size} onChange={(e) => update({ size: e.target.value })}>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="all">All</option>
          </select>
        </label>
      </Sheet>

      {error && <p className="tl-error" role="alert">{error}</p>}
      {shown?.capped && (
        <p className="tl-note" role="status">
          Showing the first 5,000. Narrow the filters to see the rest.
        </p>
      )}

      {phone ? (
        <ul className="tl-cards" aria-busy={loading}>
          {tickets.length === 0 && (
            <li className="tl-empty">No tickets found matching your filters</li>
          )}
          {tickets.map((t) => (
            <li key={t.id} className="tl-card stg-shade" data-stage={t.stage || stageOf(t)}>
              <a href={`/tickets/${t.id}`} className="tl-card-link">
                <span className="tl-card-top">
                  <span className="ticket-id">{t.ticket_number}</span>
                  <span className={`badge badge-${slug(t.status)}`}>{t.status || 'Unknown'}</span>
                </span>
                <span className="tl-card-title">{t.title}{t.steps_unowned && <> <StepsUnowned /></>}</span>
                <span className="tl-card-people">
                  <span><small>Requester</small><RequesterName ticket={t} fallback="—" /></span>
                  <span><small>Assigned</small>{t.assigned_to_name || <em>Unassigned</em>}</span>
                  <span><small>Contractor</small>{t.contractor_name || <em>None</em>}</span>
                </span>
                <span className="tl-card-foot">
                  <span className={`badge badge-${slug(t.priority)}`}>{t.priority || 'Unknown'}</span>
                  <span>{t.category || ''}</span>
                  <span>{t.created_at ? fmtDate(t.created_at) : ''}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <div className="section">
          <div className="table-wrapper">
            <table className="tl-table" aria-busy={loading}>
              <thead>
                <tr>
                  <SortHeader label="ID" sortKey="ticket_number" {...view} onSort={onSort} />
                  <SortHeader label="Title" sortKey="title" {...view} onSort={onSort} />
                  <SortHeader label="Requester" sortKey="requester" {...view} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" {...view} onSort={onSort} />
                  <SortHeader label="Priority" sortKey="priority" {...view} onSort={onSort} />
                  <SortHeader label="Category" sortKey="category" {...view} onSort={onSort} />
                  <SortHeader label="Assigned To" sortKey="assignee" {...view} onSort={onSort} />
                  <SortHeader label="Contractor" sortKey="contractor" {...view} onSort={onSort} />
                  <SortHeader label="Created" sortKey="created_at" {...view} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {tickets.length > 0 ? (
                  tickets.map((ticket) => (
                    /* Shaded by stage, from the same rule and the same colours
                       the contractor portal uses - a stage that looks one way
                       there and another way here teaches nothing. */
                    <tr key={ticket.id} className="stg-shade" data-stage={ticket.stage || stageOf(ticket)}>
                      <td><a href={`/tickets/${ticket.id}`} className="ticket-id">{ticket.ticket_number}</a></td>
                      <td>{ticket.title}{ticket.steps_unowned && <> <StepsUnowned /></>}</td>
                      <td><RequesterName ticket={ticket} fallback="" /></td>
                      <td><span className={`badge badge-${slug(ticket.status)}`}>{ticket.status || 'Unknown'}</span></td>
                      <td><span className={`badge badge-${slug(ticket.priority)}`}>{ticket.priority || 'Unknown'}</span></td>
                      <td>{ticket.category || '—'}</td>
                      <td>{ticket.assigned_to_name || <span className="tl-nobody">Unassigned</span>}</td>
                      <td>{ticket.contractor_name || <span className="tl-nobody">—</span>}</td>
                      <td>{fmtDate(ticket.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="tl-empty">
                      No tickets found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pages > 1 && (
        <nav className="tl-pages" aria-label="Pages">
          <button type="button" className="tl-page"
                  onClick={() => update({ page: view.page - 1 }, { keepPage: true })}
                  disabled={view.page <= 1}>
            ← Previous
          </button>
          <div className="tl-page-list">
            {pageButtons.map((p) => (typeof p === 'string'
              ? <span key={p} className="tl-gap" aria-hidden="true">…</span>
              : (
                <button key={p} type="button"
                        className={p === view.page ? 'tl-page is-on' : 'tl-page'}
                        aria-current={p === view.page ? 'page' : undefined}
                        onClick={() => update({ page: p }, { keepPage: true })}>
                  {p}
                </button>
              )))}
          </div>
          <button type="button" className="tl-page"
                  onClick={() => update({ page: view.page + 1 }, { keepPage: true })}
                  disabled={view.page >= pages}>
            Next →
          </button>
          <span className="tl-page-of">Page {view.page} of {pages}</span>
        </nav>
      )}
    </div>
  );
}

export default TicketsList;
